import { ref, reactive, computed, type Ref } from "vue";
import type {
  TableHeader,
  AllShopByDayListData,
  AllShopByShopListData,
  EachShopByDayListData,
  DeliverySettingListData,
  EachAreaOrders,
  Deliveries,
  SummaryTypeRadioItem,
  ShopSelectValue,
  AreaSelectValue,
} from "../types";
import { defaultEachShopByDayTableHeaders } from "../constants";
import {
  ListDailySalesAllShopByDayDocument,
  ListDailySalesAllShopByDayQueryVariables,
  ListDailySalesAllShopByDayQuery,
  ListDailySalesAllShopByShopDocument,
  ListDailySalesAllShopByShopQueryVariables,
  ListDailySalesAllShopByShopQuery,
  ListDailySalesEachShopDocument,
  ListDailySalesEachShopQueryVariables,
  ListDailySalesEachShopQuery,
  ListDeliveryTimeQuery,
  ListDeliveryTimeQueryVariables,
  ListDeliveryTimeDocument,
  DeliveryTimeListInput,
  DailySalesAllShopListCommonInput,
  DailySalesEachListCommonInput,
  OperationInput,
} from "@/generated/graphql";
import { ApolloQueryResult } from "@apollo/client/core";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetUserId,
} from "@/common/localStorageUtil";
import { DELIVERY_ITEMS } from "@/views/admin/DeliverySetting/constants";
import { generateApolloClient } from "@/common/apolloClientUtil";

const toVuetifyHeaders = (headers: TableHeader[]) =>
  headers
    .filter((header) => header.display)
    .map((header) => ({
      title: header.text,
      key: header.value,
      align: header.align,
      sortable: header.sortable,
      width: header.width,
    }));

const getRow = <T extends { raw?: unknown }>(item: T): T | NonNullable<T["raw"]> =>
  (item as { raw?: unknown }).raw ?? item;

const getHeaderAlignClass = (align?: string): string => {
  if (align === "end" || align === "right") {
    return "text-end";
  }
  if (align === "center") {
    return "text-center";
  }
  return "text-start";
};

const deliveryItemIds = new Set(DELIVERY_ITEMS.map((item) => item.delivery_id));
const isDefinedDeliveryId = (deliveryId: number): boolean =>
  deliveryItemIds.has(deliveryId);
const formatAvailability = (
  deliveryId: number,
  availability: number
): string | number => {
  if (!isDefinedDeliveryId(deliveryId)) {
    return "-";
  }
  return availability.toLocaleString();
};

interface UseSalesSummaryTableParams {
  searchConditionDeliveryDateFrom: Ref<string | undefined | null>;
  searchConditionDeliveryDateTo: Ref<string | undefined | null>;
  statusSelectedItem: Ref<string[]>;
  shopSelectValues: Ref<ShopSelectValue>;
  areaSelectValues: Ref<AreaSelectValue>;
  mode: Ref<"each_day" | "each_shop">;
  summeryTypeValues: Ref<SummaryTypeRadioItem>;
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  dispAllShopByDaylist: Ref<boolean>;
  dispAllShopByShoplist: Ref<boolean>;
  dispEachShopByDaylist: Ref<boolean>;
}

/**
 * 一覧テーブル表示に必要なデータ整形と列情報を提供する composable。
 *
 * @returns テーブル描画に利用する列定義と表示用データ
 */
export const useSalesSummaryTable = (params: UseSalesSummaryTableParams) => {
  const {
    searchConditionDeliveryDateFrom,
    searchConditionDeliveryDateTo,
    statusSelectedItem,
    shopSelectValues,
    areaSelectValues,
    mode,
    summeryTypeValues,
    errdialog,
    errMessage,
    dispAllShopByDaylist,
    dispAllShopByShoplist,
    dispEachShopByDaylist,
  } = params;

  const allShopByDaylistArray = ref<AllShopByDayListData[]>([]);
  const allShopByShoplistArray = ref<AllShopByShopListData[]>([]);
  const eachShopByDaylistArray = ref<EachShopByDayListData[]>([]);
  const deliverySettingArray = ref<DeliverySettingListData[]>([]);

  const listDeliveryTime = ref<ListDeliveryTimeQuery["listDeliveryTime"] | null | undefined>(
    undefined
  );

  const eachAreaOrders = ref<EachAreaOrders[]>([]);
  const deliveries = ref<Deliveries[]>([]);
  const tmpDeliveries = ref<Deliveries[]>([]);
  const totalDeliveries = ref<Deliveries[]>([]);
  const eachDeliveries = ref<Deliveries[]>([]);
  const averageDeliveries = ref<Deliveries[]>([]);

  const eachShopByDayTableHeaders = ref<TableHeader[]>([]);
  const eachShopByDayHeaders = computed(() =>
    toVuetifyHeaders(eachShopByDayTableHeaders.value)
  );

  /**
   * 配達時刻一覧 input
   */
  const deliveryTimeListInput: DeliveryTimeListInput = reactive({
    area_code: [],
    chain_code: localStorageGetChainCode(),
    customer_code: localStorageGetCustomerCode(),
    shop_code: "",
    limit: 10000,
    offset: 0,
  });

  /**
   * 絞り込み検索 @Click
   */
  const onClickSearch = async (initial?: boolean): Promise<void> => {
    if (mode.value === "each_day") {
      if (summeryTypeValues.value.index === "1") {
        await listDailySalesAllShopByDay();
      } else if (summeryTypeValues.value.index === "2") {
        await listDailySalesEachShopByDay();
      }
    } else {
      await listDailySalesAllShopByShop();
    }
  };

  const listDailySalesAllShopByDay = async (): Promise<void> => {
    dispAllShopByDaylist.value = false;
    const apolloClient = await generateApolloClient();
    // 全店舗日別売り上げ input
    const listInput: DailySalesAllShopListCommonInput = {
      chain_code: localStorageGetChainCode(),
      customer_code: localStorageGetCustomerCode(),
      delivery_date_from:
        searchConditionDeliveryDateFrom.value !== undefined &&
        searchConditionDeliveryDateFrom.value !== null &&
        searchConditionDeliveryDateFrom.value !== ""
          ? searchConditionDeliveryDateFrom.value
          : undefined,
      delivery_date_to:
        searchConditionDeliveryDateTo.value !== undefined &&
        searchConditionDeliveryDateTo.value !== null &&
        searchConditionDeliveryDateTo.value !== ""
          ? searchConditionDeliveryDateTo.value
          : undefined,
      delivery_status:
        statusSelectedItem.value !== undefined &&
        statusSelectedItem.value !== null &&
        statusSelectedItem.value.length > 0
          ? statusSelectedItem.value
          : undefined,
    };

    // logInput
    const operationInput: OperationInput = {
      area_code: "10",
      detail: "操作詳細",
      display_category: "画面名",
      operation: "操作",
      user_id: localStorageGetUserId(),
    };

    // 日別売り上げ(全店舗)
    let result: ApolloQueryResult<ListDailySalesAllShopByDayQuery>;
    try {
      result = await apolloClient.query<
        ListDailySalesAllShopByDayQuery,
        ListDailySalesAllShopByDayQueryVariables
      >({
        query: ListDailySalesAllShopByDayDocument,
        variables: {
          listInput: listInput,
          logInput: operationInput,
        },
        fetchPolicy: "no-cache",
      });
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      errMessage.value =
        err instanceof Error ? err.message : "予期しないエラーが発生しました。";
      return;
    }

    // 日別売り上げデータ
    allShopByDaylistArray.value = [];
    const list = result.data.listDailySalesAllShopByDay!.detail;
    const total = result.data.listDailySalesAllShopByDay!.total;
    const average = result.data.listDailySalesAllShopByDay!.average;
    list.forEach((value) => {
      if (value !== undefined && value !== null) {
        let backGroundColor = "";
        if (value.is_canceled_after_pickup == 1) {
          backGroundColor = "#FFBEDA";
        }
        let className = "text-center sales-padding";
        if (value.delivery_date.indexOf("(日)") != -1) {
          className += " text-red";
        } else if (value.delivery_date.indexOf("(土)") != -1) {
          className += " text-blue";
        }

        allShopByDaylistArray.value.push({
          deliveryDate: value.delivery_date,
          sales: value.sales.toLocaleString(),
          orderCount: value.order_count.toLocaleString(),
          salesAverage: value.sales_average.toLocaleString(),
          oriconCount: value.oricon_count.toLocaleString(),
          emptyCount: value.availability.toLocaleString(),
          backGroundColor: backGroundColor,
          className: className,
        });
      }
    });
    if (total !== undefined && total !== null) {
      allShopByDaylistArray.value.push({
        deliveryDate: "合計",
        sales: total.sales.toLocaleString(),
        orderCount: total.order_count.toLocaleString(),
        salesAverage: total.sales_average.toLocaleString(),
        oriconCount: total.oricon_count.toLocaleString(),
        emptyCount: total.availability.toLocaleString(),
        backGroundColor: "#CCFFFF",
        className: "text-center sales-padding",
      });
    }
    if (average !== undefined && average !== null) {
      allShopByDaylistArray.value.push({
        deliveryDate: "日平均",
        sales: average.sales.toLocaleString(),
        orderCount: average.order_count.toLocaleString(),
        salesAverage: "-",
        oriconCount: "-",
        emptyCount: "-",
        backGroundColor: "#FFDBC9",
        className: "text-center sales-padding",
      });
    }
    dispAllShopByDaylist.value = true;
  };

  const listDailySalesAllShopByShop = async (): Promise<void> => {
    dispAllShopByShoplist.value = false;
    const apolloClient = await generateApolloClient();
    // 全店舗店舗別売り上げ input
    const listInput: DailySalesAllShopListCommonInput = {
      chain_code: localStorageGetChainCode(),
      customer_code: localStorageGetCustomerCode(),
      delivery_date_from:
        searchConditionDeliveryDateFrom.value !== undefined &&
        searchConditionDeliveryDateFrom.value !== null &&
        searchConditionDeliveryDateFrom.value !== ""
          ? searchConditionDeliveryDateFrom.value
          : undefined,
      delivery_date_to:
        searchConditionDeliveryDateTo.value !== undefined &&
        searchConditionDeliveryDateTo.value !== null &&
        searchConditionDeliveryDateTo.value !== ""
          ? searchConditionDeliveryDateTo.value
          : undefined,
      delivery_status:
        statusSelectedItem.value !== undefined &&
        statusSelectedItem.value !== null &&
        statusSelectedItem.value.length > 0
          ? statusSelectedItem.value
          : undefined,
    };

    // logInput
    const operationInput: OperationInput = {
      area_code: "10",
      detail: "操作詳細",
      display_category: "画面名",
      operation: "操作",
      user_id: localStorageGetUserId(),
    };

    // 日別売り上げ(全店舗店舗別)
    let result: ApolloQueryResult<ListDailySalesAllShopByShopQuery>;
    try {
      result = await apolloClient.query<
        ListDailySalesAllShopByShopQuery,
        ListDailySalesAllShopByShopQueryVariables
      >({
        query: ListDailySalesAllShopByShopDocument,
        variables: {
          listInput: listInput,
          logInput: operationInput,
        },
        fetchPolicy: "no-cache",
      });
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      errMessage.value = err instanceof Error ? err.message : "エラーが発生しました。";
      return;
    }

    // 日別売り上げデータ
    allShopByShoplistArray.value = [];
    const list = result.data.listDailySalesAllShopByShop!.detail;
    const total = result.data.listDailySalesAllShopByShop!.total;
    const average = result.data.listDailySalesAllShopByShop!.average;

    eachAreaOrders.value = [];
    let backGroundColor = "";
    list.forEach((value) => {
      if (value !== undefined && value !== null) {
        value!.deliveries!.forEach((delivery) => {
          backGroundColor = "";
          if (delivery!.is_canceled_after_pickup == 1) {
            backGroundColor = "#FFBEDA";
          }
          let deliveryName = "";
          if (delivery!.delivery_id == 0) {
            deliveryName = "都度便";
          } else {
            let no_string = String(delivery!.delivery_id).replace(/[0-9]/g, function (s) {
              return String.fromCharCode(s.charCodeAt(0) + 0xfee0);
            });
            deliveryName = no_string + "便";
          }
          eachAreaOrders.value.push({
            areaName: delivery!.area_name,
            orderName: deliveryName,
            eachOrderCount: delivery!.order_count!.toLocaleString(),
            backGroundColor: backGroundColor.toLocaleString(),
          });
        });
        backGroundColor = "";
        if (value.is_canceled_after_pickup == 1) {
          backGroundColor = "#FFBEDA";
        }
        allShopByShoplistArray.value.push({
          shopCode: value.shop_code || "",
          shopName: value.shop_name || "",
          sales: value.sales.toLocaleString(),
          orderCount: value.order_count.toLocaleString(),
          backGroundColor: backGroundColor,
          byAreaOrders: eachAreaOrders.value,
        });
        eachAreaOrders.value = [];
      }
    });
    eachAreaOrders.value = [];
    if (total !== undefined && total !== null) {
      eachAreaOrders.value.push({
        areaName: "-",
        orderName: "-",
        eachOrderCount: "-",
        backGroundColor: "#CCFFFF",
      });
      allShopByShoplistArray.value.push({
        shopCode: "合計",
        shopName: "",
        sales: total.sales.toLocaleString(),
        orderCount: total.order_count.toLocaleString(),
        backGroundColor: "#CCFFFF",
        byAreaOrders: eachAreaOrders.value,
      });
    }
    eachAreaOrders.value = [];
    if (average !== undefined && average !== null) {
      eachAreaOrders.value.push({
        areaName: "-",
        orderName: "-",
        eachOrderCount: "-",
        backGroundColor: "#FFDBC9",
      });
      allShopByShoplistArray.value.push({
        shopCode: "日平均",
        shopName: "",
        sales: average.sales.toLocaleString(),
        orderCount: average.order_count.toLocaleString(),
        backGroundColor: "#FFDBC9",
        byAreaOrders: eachAreaOrders.value,
      });
    }
    dispAllShopByShoplist.value = true;
  };

  /**
   * 配達時間一覧取得処理
   */
  const listDeliverySettingList = async (): Promise<void> => {
    listDeliveryTime.value = [];

    const shopCode = shopSelectValues.value?.shop_code ?? "";
    if (!shopCode) {
      deliveryTimeListInput.shop_code = "";
      deliveryTimeListInput.area_code = [];
      deliverySettingArray.value = [];
      return;
    }
    deliveryTimeListInput.shop_code = shopCode;
    deliveryTimeListInput.area_code = [];

    if (areaSelectValues.value !== undefined && areaSelectValues.value !== null && areaSelectValues.value.area_code !== "") {
      deliveryTimeListInput.area_code = [areaSelectValues.value.area_code];
    }

    deliverySettingArray.value = [];
    //graphql接続
    const apolloClient = await generateApolloClient();
    // 配達時間一覧取得 [引数:input参照]
    let deliveryTime: ApolloQueryResult<ListDeliveryTimeQuery>;
    try {
      deliveryTime = await apolloClient.query<ListDeliveryTimeQuery, ListDeliveryTimeQueryVariables>({
        query: ListDeliveryTimeDocument,
        variables: {
          input: deliveryTimeListInput,
        },
        fetchPolicy: "no-cache",
      });
    } catch (err) {
      console.error(err);
      return;
    }
    let delivery_id_array: number[] = [];
    if (deliveryTime.data.listDeliveryTime!.length > 0) {
      listDeliveryTime.value = deliveryTime.data.listDeliveryTime!;
      listDeliveryTime.value!.forEach((value) => {
        if (value !== undefined && value !== null) {
          if (delivery_id_array.indexOf(value.delivery_id) == -1) {
            delivery_id_array.push(value.delivery_id);
          }
        }
      });
      deliverySettingArray.value = [];
      delivery_id_array.forEach((delivery_id) => {
        let delivery_id_lavel = "";
        if (delivery_id !== 0) {
          let no_string = String(delivery_id).replace(/[0-9]/g, function (s) {
            return String.fromCharCode(s.charCodeAt(0) + 0xfee0);
          });
          delivery_id_lavel = no_string + "便";
        } else {
          delivery_id_lavel = "都度便";
        }
        deliverySettingArray.value.push({
          delivery_id: delivery_id,
          delivery_id_label: delivery_id_lavel,
        });
      });
    }
  };

  const listDailySalesEachShopByDay = async (): Promise<void> => {
    dispEachShopByDaylist.value = false;
    eachShopByDayTableHeaders.value = [];
    const apolloClient = await generateApolloClient();
    // 店舗別日別売り上げ input
    const listInput: DailySalesEachListCommonInput = {
      area_code:
        areaSelectValues.value !== undefined &&
        areaSelectValues.value !== null &&
        areaSelectValues.value.area_code !== ""
          ? areaSelectValues.value.area_code
          : undefined,
      chain_code: localStorageGetChainCode(),
      customer_code: localStorageGetCustomerCode(),
      delivery_date_from:
        searchConditionDeliveryDateFrom.value !== undefined &&
        searchConditionDeliveryDateFrom.value !== null &&
        searchConditionDeliveryDateFrom.value !== ""
          ? searchConditionDeliveryDateFrom.value
          : undefined,
      delivery_date_to:
        searchConditionDeliveryDateTo.value !== undefined &&
        searchConditionDeliveryDateTo.value !== null &&
        searchConditionDeliveryDateTo.value !== ""
          ? searchConditionDeliveryDateTo.value
          : undefined,
      delivery_status:
        statusSelectedItem.value !== undefined &&
        statusSelectedItem.value !== null &&
        statusSelectedItem.value.length > 0
          ? statusSelectedItem.value
          : undefined,
      shop_code:
        shopSelectValues.value.shop_code !== undefined &&
        shopSelectValues.value.shop_code !== null &&
        shopSelectValues.value.shop_code !== ""
          ? shopSelectValues.value.shop_code
          : "",
    };

    // logInput
    const operationInput: OperationInput = {
      area_code: "10",
      detail: "操作詳細",
      display_category: "画面名",
      operation: "操作",
      user_id: localStorageGetUserId(),
    };

    // 日別売り上げ(店舗別)
    eachShopByDaylistArray.value = [];
    let result: ApolloQueryResult<ListDailySalesEachShopQuery>;
    try {
      result = await apolloClient.query<
        ListDailySalesEachShopQuery,
        ListDailySalesEachShopQueryVariables
      >({
        query: ListDailySalesEachShopDocument,
        variables: {
          listInput: listInput,
          logInput: operationInput,
        },
        fetchPolicy: "no-cache",
      });
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error(err);
        errdialog.value = true;
        errMessage.value = err.message;
      } else {
        console.error("不明なエラー:", err);
        errdialog.value = true;
        errMessage.value = "予期しないエラーが発生しました。";
      }
      return;
    }

    // 日別売り上げデータ
    const list = result.data.listDailySalesEachShop!.detail;
    const total = result.data.listDailySalesEachShop!.total;
    const average = result.data.listDailySalesEachShop!.average;

    eachShopByDayTableHeaders.value = [];
    defaultEachShopByDayTableHeaders.forEach((value) => {
      eachShopByDayTableHeaders.value.push({
        id: value.id,
        display: value.display,
        text: value.text,
        settingText: value.settingText,
        align: value.align,
        sortable: value.sortable,
        orderDisabled: value.orderDisabled,
        order: value.order,
        orderKey: value.orderKey,
        value: value.value,
        width: value.width,
      });
    });

    await listDeliverySettingList();

    // 売上データに含まれる delivery_id のうち、配達時刻設定に存在しないもの（99便等の特別便）を
    // deliverySettingArray に追加し、動的列が生成されるようにする
    const existingDeliveryIds = new Set(
      deliverySettingArray.value.map((d) => d.delivery_id)
    );
    const additionalDeliveryIds: number[] = [];
    list.forEach((value) => {
      value?.deliveries?.forEach((delivery) => {
        if (
          !existingDeliveryIds.has(delivery.delivery_id) &&
          !additionalDeliveryIds.includes(delivery.delivery_id)
        ) {
          additionalDeliveryIds.push(delivery.delivery_id);
        }
      });
    });
    // delivery_id 昇順でソートして追加
    additionalDeliveryIds.sort((a, b) => a - b);
    additionalDeliveryIds.forEach((deliveryId) => {
      let delivery_id_label = "";
      if (deliveryId !== 0) {
        let no_string = String(deliveryId).replace(/[0-9]/g, function (s) {
          return String.fromCharCode(s.charCodeAt(0) + 0xfee0);
        });
        delivery_id_label = no_string + "便";
      } else {
        delivery_id_label = "都度便";
      }
      deliverySettingArray.value.push({
        delivery_id: deliveryId,
        delivery_id_label: delivery_id_label,
      });
    });

    deliverySettingArray.value.forEach((value, index) => {
      eachShopByDayTableHeaders.value.push({
        id: 6 + index,
        display: true,
        text: "個別売上金額(" + value.delivery_id_label + ")",
        settingText: "個別売上金額(" + value.delivery_id_label + ")",
        align: "center",
        sortable: false,
        orderDisabled: false,
        order: "",
        orderKey: "",
        value: "each_order_sales" + (index + 1),
        width: 160,
      });
      eachShopByDayTableHeaders.value.push({
        id: 7 + index,
        display: true,
        text: "個別注文件数(" + value.delivery_id_label + ")",
        settingText: "個別注文件数(" + value.delivery_id_label + ")",
        align: "center",
        sortable: false,
        orderDisabled: false,
        order: "",
        orderKey: "",
        value: "each_order_order_count" + (index + 1),
        width: 160,
      });
      eachShopByDayTableHeaders.value.push({
        id: 8 + index,
        display: true,
        text: "個別空き数(" + value.delivery_id_label + ")",
        settingText: "個別空き数(" + value.delivery_id_label + ")",
        align: "center",
        sortable: false,
        orderDisabled: false,
        order: "",
        orderKey: "",
        value: "each_order_availability" + (index + 1),
        width: 160,
      });
    });

    averageDeliveries.value = [];
    tmpDeliveries.value = [];
    eachDeliveries.value = [];
    totalDeliveries.value = [];
    list.forEach((value) => {
      value?.deliveries!.forEach((delivery) => {
        tmpDeliveries.value.push({
          availability: delivery.availability,
          deliveryId: delivery.delivery_id,
          isDeliveryGroup: delivery.is_delivery_group,
          orderCount: delivery.order_count,
          sales: delivery.sales,
          backGroundColor: "",
        });
      });
    });

    tmpDeliveries.value.sort(
      (a, b) => (a["deliveryId"] as number) - (b["deliveryId"] as number)
    );
    let oldId = -1;
    let availability = 0;
    let isDeliveryGroup = 0;
    let orderCount = 0;
    let sales = 0;
    tmpDeliveries.value.forEach((tmp, i) => {
      if (oldId != (tmp.deliveryId as number) && i != 0) {
        eachDeliveries.value.push({
          availability: availability,
          deliveryId: oldId,
          isDeliveryGroup: isDeliveryGroup,
          orderCount: orderCount,
          sales: sales,
          backGroundColor: "",
        });
        if (oldId !== 0) {
          let no_string = String(oldId).replace(/[0-9]/g, function (s) {
            return String.fromCharCode(s.charCodeAt(0) + 0xfee0);
          });
          console.log(no_string);
        }

        availability = 0;
        isDeliveryGroup = 0;
        orderCount = 0;
        sales = 0;
      }
      oldId = Number(tmp.deliveryId);
      availability += Number(tmp.availability);
      if (tmp.isDeliveryGroup != 0) {
        isDeliveryGroup = Number(tmp.isDeliveryGroup);
      }
      orderCount += Number(tmp.orderCount);
      sales += Number(tmp.sales);
    });
    eachDeliveries.value.push({
      availability: availability,
      deliveryId: oldId,
      isDeliveryGroup: isDeliveryGroup,
      orderCount: orderCount,
      sales: sales,
      backGroundColor: "",
    });

    deliverySettingArray.value.forEach((setting) => {
      let match_flg = false;
      eachDeliveries.value.forEach((each) => {
        if (setting.delivery_id == (each.deliveryId as number)) {
          match_flg = true;
          totalDeliveries.value.push({
            availability: formatAvailability(
              each.deliveryId as number,
              Number(each.availability)
            ),
            deliveryId: each.deliveryId,
            isDeliveryGroup: each.isDeliveryGroup,
            orderCount: Number(each.orderCount).toLocaleString(),
            sales: Number(each.sales).toLocaleString(),
            backGroundColor: "",
          });
          return true;
        }
      });
      if (match_flg === false) {
        totalDeliveries.value.push({
          availability: isDefinedDeliveryId(setting.delivery_id) ? 0 : "-",
          deliveryId: setting.delivery_id,
          isDeliveryGroup: 0,
          orderCount: 0,
          sales: 0,
          backGroundColor: "",
        });
      }
    });

    deliverySettingArray.value.forEach(() => {
      averageDeliveries.value.push({
        availability: "-",
        deliveryId: "-",
        isDeliveryGroup: "-",
        orderCount: "-",
        sales: "-",
        backGroundColor: "",
      });
    });

    deliveries.value = [];
    list.forEach((value) => {
      deliverySettingArray.value.forEach((setting) => {
        let isExist = false;
        value?.deliveries?.forEach((delivery) => {
          if (setting.delivery_id == delivery.delivery_id) {
            let backGroundColor = "";
            if (delivery.is_canceled_after_pickup == 1) {
              backGroundColor = "#FFBEDA";
            }
            deliveries.value.push({
              availability: formatAvailability(
                delivery.delivery_id,
                delivery.availability
              ),
              deliveryId: delivery.delivery_id,
              isDeliveryGroup: delivery.is_delivery_group,
              orderCount: delivery.order_count.toLocaleString(),
              sales: delivery.sales.toLocaleString(),
              backGroundColor: backGroundColor,
            });
            isExist = true;
          }
        });
        if (isExist === false) {
          deliveries.value.push({
            availability: isDefinedDeliveryId(setting.delivery_id) ? 0 : "-",
            deliveryId: setting.delivery_id,
            isDeliveryGroup: 0,
            orderCount: 0,
            sales: 0,
            backGroundColor: "",
          });
        }
      });
      if (value !== undefined && value !== null) {
        let backGroundColor = "";
        if (value.is_canceled_after_pickup == 1) {
          backGroundColor = "#FFBEDA";
        }
        let className = "text-center sales-padding";
        if (value.delivery_date.indexOf("(日)") != -1) {
          className += " text-red";
        } else if (value.delivery_date.indexOf("(土)") != -1) {
          className += " text-blue";
        }
        eachShopByDaylistArray.value.push({
          deliveryDate: value.delivery_date,
          sales: value.sales.toLocaleString(),
          orderCount: value.order_count.toLocaleString(),
          salesAverage: value.sales_average.toLocaleString(),
          oriconCount: value.oricon_count.toLocaleString(),
          backGroundColor: backGroundColor,
          deliveries: deliveries.value,
          className: className,
        });
      }
      deliveries.value = [];
    });

    if (total !== undefined && total !== null) {
      eachShopByDaylistArray.value.push({
        deliveryDate: "合計",
        sales: total.sales.toLocaleString(),
        orderCount: total.order_count.toLocaleString(),
        salesAverage: total.sales_average.toLocaleString(),
        oriconCount: total.oricon_count.toLocaleString(),
        backGroundColor: "#CCFFFF",
        deliveries: totalDeliveries.value,
        className: "text-center sales-padding",
      });
    }
    if (average !== undefined && average !== null) {
      eachShopByDaylistArray.value.push({
        deliveryDate: "日平均",
        sales: average.sales.toLocaleString(),
        orderCount: average.order_count.toLocaleString(),
        salesAverage: "-",
        oriconCount: "-",
        backGroundColor: "#FFDBC9",
        deliveries: averageDeliveries.value,
        className: "text-center sales-padding",
      });
    }
    dispEachShopByDaylist.value = true;
  };

  return {
    allShopByDaylistArray,
    allShopByShoplistArray,
    eachShopByDaylistArray,
    deliverySettingArray,
    listDeliveryTime,
    eachShopByDayTableHeaders,
    eachShopByDayHeaders,
    onClickSearch,
    toVuetifyHeaders,
    getRow,
    getHeaderAlignClass,
  };
};
